/**
 * PrinterService - This service handles the printing of dockets, queueing of print jobs, and checking of printer status.
 *
 * Separate functions/services for Bluetooth printer functions?
 * The main reason why there are different functions for BT and IP printers is that there can only be 1 active BT connection at any given time.
 * This is unlike IP printers in which the connection will be persistent.
 *
 * Another reason why BT functions use services (PrintService and BluetoothPrinterStatusService) is because
 * they require the BluetoothSerial plugin injected, unlike IP printer which only requires the Socket property from window object.
 *
 *
 * Why queue BT print jobs?
 * Ready to Serve supports single BT connection, hence, must queue the PrintJobs for the other non-active BT printers.
 * Until the current PrintJob is completed and the next target printer is available, pending PrintJobs will remain in the queue.
 *
 * For Ethernet/IP printers, there's no need to queue and as it utilises Spool Printing.
 *
 *
 */

import { Injectable } from '@angular/core';
import { Printer } from 'src/app/models/printer/printer.model';
import { BluetoothSerial } from '@ionic-native/bluetooth-serial/ngx';
import { Platform, AlertController, ModalController } from '@ionic/angular';
import { StorageService } from '../../utils/storage/storage.service';
import { SettingsService } from '../settings/settings.service';
import { PRINTER_COMMAND, PRINTER_MESSAGE, PRINTER_STATUS, PRINTER_TYPE } from './printer.constants';
import { AlertQueueService } from '../../utils/prompt-queue/alert-queue.service';
import { WatcherService } from '../../utils/watcher/watcher.service';
import {
  ACTIVE_PRINTERS_CHANGED,
  PRINTER_STATUS_PING,
  SETTING_ACTIVE_PRINTER,
} from '../../utils/watcher/watcher-event';
import { HubAlertModule } from '../../utils/prompt-queue/alert-queue.module.constant';
import { Subject, Subscription } from 'rxjs';
import { PrinterGroup } from 'src/app/models/printer/printer-group.model';
import {
  IPrinterStatus,
  IPrintJobBacklog,
  IPrintResponse,
  IPrintStringObj,
  IStatusCheckBacklog,
} from './printer.interface';
import { BluetoothPrinterStatusService } from './bluetooth-printer-status.service';
import { environment } from 'src/environments/environment';
import { CoreService } from '../core/core.service';
import { AuthService } from '../auth/auth.service';
import { AnalyticsService } from '../../utils/analytics/analytics.service';
import { ModalNetworkPrinterComponent } from 'src/app/components/modal-forms/modal-network-printer/modal-network-printer.component';
import EscPosEncoder from 'esc-pos-encoder';

import size from 'lodash/size';
import filter from 'lodash/filter';
import each from 'lodash/each';
import findIndex from 'lodash/findIndex';

export type ToggleValue = 'ON' | 'OFF';

@Injectable({
  providedIn: 'root',
})
export class PrinterService {
  public settingNewPrinter: boolean = false;

  /** suppress No Dockets Created error message */
  public showNoDocketErrorMessage: ToggleValue = 'ON';

  /** Storage Keys */
  // To be deprecated - Used to pull the active printer
  public static ACTIVE_PRINTER_STORAGE_KEY: string = 'active_printer';

  // TO BE DEPRECATED - Key to pull all paired printers from storage
  public static PAIRED_PRINTERS_STORAGE_KEY: string = 'paired_printers';

  // Key to pull all paired printers from storage
  public static PAIRED_PRINTERS_STORAGE_KEY_2: string = 'paired_printers2';

  // Key to pull all saved printer group of the bump screen
  public static PRINTER_GROUPS_STORAGE_KEY: string = 'printer_groups';

  // Key to show the No Docket Creted error
  public static SHOW_DOCKET_ERROR_STORAGE_KEY: string = 'show_docket_error';

  // list of available printers
  private _availablePrinters: Printer[] = [];

  /** The count of active printers */
  public activePrintersCount: number = 0;

  /** Flagger to indicate if there are any active printers */
  private _hasActivePrinters: boolean = false;

  /** Connect subscription of the active BT printer */
  private _bluetoothConnectSubscription: Subscription;

  /** MAC address of the active printer */
  private _bluetoothConnectedAddress: string;

  /** Subscription to notify a completed print job and move on to the next */
  private _bluetoothNextJobSubject: Subject<boolean> = new Subject();

  /** Flagger to indicate the bluettoth module is busy */
  private _bluetoothConnectionBusy: boolean = false;

  /** The list of queued Print Jobs. A print job is equal to 1 docket. */
  private _bluetoothPrintJobQueue: IPrintJobBacklog[] = [];

  /** The list of queued Printer Status Check Jobs. */
  private _bluetoothPrinterPingQueue: IStatusCheckBacklog[] = [];

  /** All the available print groups */
  private _printerGroups: PrinterGroup[] = [];

  /** Default subscriptions */
  private _BTStatusCheckCompletedSub: Subscription;
  private _BTPrinterPingSub: Subscription;
  private _BTPrinterNextJobSub: Subscription;

  constructor(
    public bluetooth: BluetoothSerial,
    public platform: Platform,
    public storage: StorageService,
    public settings: SettingsService,
    public core: CoreService,
    public alertCtrl: AlertController,
    public alertQueue: AlertQueueService,
    public bluetoothPrinterStatus: BluetoothPrinterStatusService,
    public modalCtrl: ModalController,
    public watcher: WatcherService,
    public auth: AuthService,
    public analytics: AnalyticsService,
  ) {
    this._BTPrinterPingSub = this.watcher.on(PRINTER_STATUS_PING).subscribe((printerToPing: Printer) => {
      printerToPing && this.pingPrinterByAddress(printerToPing.Address);
    });

    this._BTPrinterNextJobSub = this._bluetoothNextJobSubject.subscribe(async () => {
      if (size(this._bluetoothPrintJobQueue) > 0) {
        const nextToPrint: IPrintJobBacklog = this._bluetoothPrintJobQueue.shift();
        if (size(nextToPrint) > 0) {
          const resp = await this.printReceiptByAddress(
            nextToPrint.printerAddress,
            nextToPrint.toPrint,
            nextToPrint.fromFailedAttempt,
          );

          // Call to bubble up to main resolver and move on to the next JOB
          nextToPrint.resolveFn(resp);
        }
      } else if (size(this._bluetoothPrinterPingQueue) > 0) {
        const nexToCheck: IStatusCheckBacklog = this._bluetoothPrinterPingQueue.shift();
        if (size(nexToCheck) > 0) {
          // never backlog a backlog
          const resp = await this.pingPrinterByAddress(nexToCheck.printerAddress);

          // Call to bubble up to main resolver and move on to the next JOB
          nexToCheck.resolveFn(resp);
        }
      }
    });

    this.checkStorage();
  }

  async checkStorage() {
    const errorMsgStorage = await this.storage.load(PrinterService.SHOW_DOCKET_ERROR_STORAGE_KEY);
    if (errorMsgStorage) {
      this.showNoDocketErrorMessage = errorMsgStorage;
    }
  }

  /**
   * Prepares the printer to be used and sends the data to the printer.
   *
   * This function will also queue BT printing jobs if the BT module is still busy.
   * @param macAddress_IP - The printer address to use
   * @param toPrint - The PrintStringObj to print
   * @param fromFailedAttempt - Flagger if request is from a failed request, queued.
   */
  public printingJobsOnQueue: number = 0;
  printReceiptByAddress(
    macAddress_IP: string,
    toPrint: IPrintStringObj,
    fromFailedAttempt: boolean = false,
  ): Promise<IPrintResponse> {
    return new Promise(async printDocketResolver => {
      this.printingJobsOnQueue++;

      // Create a printing job Queue for Bluetooth
      const printerToUse: Printer = this.getPrinterByAddress(macAddress_IP);
      if (printerToUse && printerToUse.Type == PRINTER_TYPE.BT) {
        // if busy then add to queue
        if (this._bluetoothConnectionBusy) {
          this._bluetoothPrintJobQueue.push({
            printerAddress: macAddress_IP,
            toPrint: toPrint,
            fromFailedAttempt: false,
            resolveFn: resp => {
              this.printingJobsOnQueue--;
              printDocketResolver(resp);
            },
          });

          return;
        } else {
          // lock the bluetooth
          this._bluetoothConnectionBusy = true;
        }
      }

      /** Work on preparing/initialising the printer */
      /** If BT, then it will try to connect the printer first before writing commands */
      let printResp: IPrintResponse = await this.preparePrinter(macAddress_IP);

      // If printer is ready, then proceed with the Printing job
      if (printResp && printResp.success) {
        printResp = await this.writeDocket(toPrint, printerToUse);
      }

      /**
       * Free up the bluetooth module after every printing regardless of result.
       * */
      if (printResp && printerToUse && printerToUse.Type == PRINTER_TYPE.BT) {
        this._bluetoothConnectionBusy = false;

        if (!printResp.success && !fromFailedAttempt) {
          this.analytics.trackEvent(
            'Bluetooth',
            `Print failed (queued retry): ${printResp.message || 'Unknown'} | address: ${macAddress_IP}`,
            this.auth.Tracking_key,
          );
          // add it to QUEUE
          this._bluetoothPrintJobQueue.unshift({
            printerAddress: macAddress_IP,
            toPrint: toPrint,
            fromFailedAttempt: true,
            resolveFn: resp => {
              this.printingJobsOnQueue--;

              // wrap the resolve function with another function
              // to do additional logic. e.g analytics
              printDocketResolver(resp);
            },
          });

          this._bluetoothNextJobSubject.next(true);
          return;
        }

        this._bluetoothNextJobSubject.next(true);
      }

      // intialise the EXTRA property if it is empty
      if (size(printResp.extra) == 0) {
        printResp.extra = {};
      }

      // extend the printer response and capture the printer data to return
      printResp.extra['printer'] = {
        address: macAddress_IP,
        name: printerToUse ? printerToUse.Name : macAddress_IP,
        fromFailedAttempt: fromFailedAttempt,
      };

      this.printingJobsOnQueue--;
      printDocketResolver(printResp);
    });
  }

  /**
   * Invokes a test print to a target printer
   * @param address - The printer address
   */
  async sendTestPrintByAddress(address: string): Promise<IPrintResponse> {
    return new Promise(async resolve => {
      const printerToUse: Printer = this.getPrinterByAddress(address);
      if (printerToUse) {
        const printRepeat: number = printerToUse.Print_repeat || 1;

        const br = PRINTER_COMMAND.br;
        const headerText: string = `TEST PRINT - PRINTER INFO${br}${br}`;
        const testName: string = `Name: ${printerToUse.Name}${br}`;
        const testAddress: string = `Address: ${printerToUse.Address}${br}`;
        const testCodePage: string = `Code: ${this.core.Active_server.print_codepage}${br}`;

        const testPrintContent: string = `${headerText}${testName}${testAddress}${testCodePage}`;
        const result = await this.printReceiptByAddress(
          address,
          this.formatDocketToPrint(testPrintContent, printRepeat),
        );
        resolve(result);
      } else {
        resolve({
          success: false,
          message: PRINTER_MESSAGE.PRINTER_NO_PAIRED,
        });
      }
    });
  }

  /**
   * Creates multiple copies of string to print
   * @param value The string to duplicate
   * @param repeatCount Number of times to duplicate
   * @returns The string output with print commands
   */
  private _duplicatePrinterDocket(value: string, repeatCount: number): string {
    let docketHolder: string[] = [];
    for (let index: number = 0; index < repeatCount; index++) {
      docketHolder.push(value);
    }

    return docketHolder.join(PRINTER_COMMAND.br + '\n\n\n\n\n\n' + PRINTER_COMMAND.cut);
  }

  /**
   * Function that sends the data to buffer ready to be printed
   * @param toPrint Print object to be printed
   * @param printer The target printer
   * @returns IPrintResponse
   */
  async writeDocket(toPrint: IPrintStringObj, printer: Printer): Promise<IPrintResponse> {
    return new Promise(async writeResolve => {
      /** If there's nothing to print, don't bother */
      if (!size(toPrint) || !size(toPrint.data)) {
        writeResolve({
          success: false,
          message: PRINTER_MESSAGE.EMPTY_DATA,
        });
        return;
      }

      if (!printer) {
        writeResolve({
          success: false,
          message: PRINTER_MESSAGE.NO_PRINTER_SELECTED,
        });
      } else {
        const encoder = new EscPosEncoder();

        /** Check if the current server is using a printer code page */
        let encodedResult;
        if (this.core.Active_server.print_codepage != 'default') {
          encodedResult = encoder
            .initialize()
            .codepage(this.core.Active_server.print_codepage)
            .text(toPrint.data)
            .encode();
        } else {
          encodedResult = encoder.initialize().text(toPrint.data).encode();
        }

        if (printer.Type == PRINTER_TYPE.IP) {
          const printingResp: IPrintResponse = await printer.writeMessage(encodedResult);
          writeResolve({
            success: printingResp.success,
            message: printingResp.success ? '' : printingResp.message,
          });
        } else {
          // IF BLUETOOTH

          // EXPERIMENTAL - Probably a more optimal way of waiting for bluetooth that it is done sending the data
          let writeCompleteSub;
          const promiseGsR = new Promise(resolve => {
            writeCompleteSub = this.bluetooth.subscribeRawData().subscribe(data => {
              if (writeCompleteSub) writeCompleteSub.unsubscribe();
              resolve('GS r Ready');
            });
          });

          const writeTime = new Date().valueOf();
          this.bluetooth.write(encodedResult).then(
            writeData => {
              const result = encoder
                .raw([0x1d, 0x72, 1]) // GS r
                .encode();

              Promise.race([
                promiseGsR,
                new Promise(resolve => setTimeout(() => resolve('Timeout'), printer.Print_repeat * 2000)),
              ]).then(stat => {
                writeCompleteSub && writeCompleteSub.unsubscribe();
                const now = new Date().valueOf();
                // console.log("WRITE STAT", printer.Address, stat, (now - writeTime) / 1000);

                writeResolve({
                  success: true,
                  message: '',
                });
              });

              // establish the wait
              this.bluetooth.write(result).then(() => {
                // console.log("GS r SENT");
              });
            },
            err => {
              if (writeCompleteSub) writeCompleteSub.unsubscribe();

              const errMsg = err && (err.message || err) ? String(err.message || err) : 'Unknown';
              this.analytics.trackEvent('Bluetooth', `Print write failed: ${errMsg}`, this.auth.Tracking_key);
              // add a delay before resolving so bluetooth manager doesn't clear the buffer
              setTimeout(() => {
                writeResolve({
                  success: false,
                  message: err.message || err,
                });
              }, 2000);
            },
          );
        }
      }
    });
  }

  /**
   * Format the data to IPrintStringObj, this will be appended with PRINT commands
   *
   * Order param will be deprecated soon.
   *
   * @param toPrint The data to format, this can be an ORDER or string
   * @param repeatCount Number of times to duplicate the print
   * @returns IPrintStringObj
   */
  formatDocketToPrint(toPrint: string | string[], repeatCount: number): IPrintStringObj {
    let receiptStr: string;
    if (typeof toPrint === 'string') {
      receiptStr = this._duplicatePrinterDocket(toPrint, repeatCount);
    } else if (Array.isArray(toPrint)) {
      // for split dockets
      receiptStr = this._duplicatePrinterDocket(toPrint.join(PRINTER_COMMAND.br), repeatCount);
    }

    // 6 break lines and CUT
    const PRINT_START: string = PRINTER_COMMAND.br + PRINTER_COMMAND.f03;
    const PRINT_END: string = PRINTER_COMMAND.f08 + PRINTER_COMMAND.cut;

    // format to print object
    return {
      data: `${PRINT_START}${PRINTER_COMMAND.default_line_spacing}${PRINTER_COMMAND.h01}${receiptStr}${PRINTER_COMMAND.h00}${PRINT_END}`,
    };
  }

  /***** PRINTER MANAGEMENT START *****/

  /**
   * Retrieve the printer object by the device address
   * @param macAddress_IP - The printer address
   */
  getPrinterByAddress(macAddress_IP: string): Printer | null {
    const printer: number = findIndex(this._availablePrinters, (device: Printer) => device.Address == macAddress_IP);
    if (printer > -1) {
      return this._availablePrinters[printer];
    }

    return null;
  }

  /**
   * Save the printer data to storage
   */
  savePrinters(): void {
    const printersToStore: any[] = [];
    if (size(this._availablePrinters) > 0) {
      each(this._availablePrinters, (printer: Printer) => {
        printersToStore.push(printer.export());
      });
    }

    this.storage.save(PrinterService.PAIRED_PRINTERS_STORAGE_KEY_2, printersToStore);
  }

  /**
   * Checks if the target printer can accept requests to print. If it can accept requets then
   * prepare/initialise the printer.
   * @param address The address of the target printer
   * @param statusContinueOnSameAddress Ignore the printer busy status if target and connected BT printer is the same
   * @param statusVerifyLastChecked Reverify the printer status
   * @returns Promise response of IPrintResponse
   */
  async preparePrinter(
    address: string,
    statusContinueOnSameAddress: boolean = true,
    statusVerifyLastChecked: boolean = false,
  ): Promise<IPrintResponse> {
    return new Promise(async prepareResolve => {
      const printer: Printer = this.getPrinterByAddress(address);
      if (printer) {
        // check if printer is paired
        if (printer.Active) {
          // check printer is active

          // Try to connect to the printer first if this is BT
          if (printer.Type == PRINTER_TYPE.BT) {
            // Add a 30-second timeout for attempting to connect to printer
            const connectBTResp: IPrintResponse = await Promise.race([
              /** Add a timeout to kill the connection of printer, fires after 15 seconds */
              new Promise<IPrintResponse>(resolve => {
                setTimeout(resolve, 15000, {
                  success: false,
                  message: PRINTER_MESSAGE.FAILED_TO_CONNECT_IN_TIME,
                });
              }),

              /** Bluetooth printer connect request */
              this.connectBluetoothPrinter(printer.Address, false),
            ]);

            // resolve the connection attempt if it failedx
            if (!connectBTResp || !connectBTResp.success) {
              printer.updateStatus({
                success: false,
                status: connectBTResp.message,
              });

              connectBTResp['data'] = printer;
              prepareResolve(connectBTResp);
              return;
            }
          }

          // now check the status check
          // never ever backlog a request from printing
          this._checkPrinterStatusByAddress(printer.Address, statusContinueOnSameAddress, statusVerifyLastChecked).then(
            (statusCheck: IPrinterStatus) => {
              // update the printer status
              printer.updateStatus(statusCheck);
              prepareResolve({
                success: statusCheck ? statusCheck.success : false,
                message: statusCheck ? statusCheck.status : PRINTER_STATUS.UNKNOWN,
                data: printer,
              });
            },
          );
        } else {
          prepareResolve({
            success: false,
            message: PRINTER_MESSAGE.PRINTER_NOT_ACTIVE,
          });
        }
      } else {
        prepareResolve({
          success: false,
          message: PRINTER_MESSAGE.PRINTER_NO_PAIRED,
        });
      }
    });
  }

  /**
   * Invoke a printer check to retrieve the status of the printer
   * @param macAddress_IP The target printer address
   * @returns Promise response of IPrinterStatus
   */
  async pingPrinterByAddress(macAddress_IP: string): Promise<IPrinterStatus> {
    return new Promise(async pingPrinterResolver => {
      // Create a printing job Queue for Bluetooth
      const printerToUse: Printer = this.getPrinterByAddress(macAddress_IP);
      if (printerToUse && printerToUse.Type == PRINTER_TYPE.BT) {
        // if busy then add to queue
        if (this._bluetoothConnectionBusy) {
          // add status check to backlog
          this._bluetoothPrinterPingQueue.push({
            printerAddress: macAddress_IP,
            resolveFn: resp => {
              // wrap the resolve function with another function
              // to do additional logic. e.g analytics
              pingPrinterResolver(resp);
            },
          });

          return;
        } else {
          // lock the bluetooth
          this._bluetoothConnectionBusy = true;
        }
      }

      /** Work on preparing/initialising the printer */
      /** If BT, then it will try to connect the printer first before writing commands */
      let pingResp: IPrintResponse = await this.preparePrinter(macAddress_IP);
      if (size(this._bluetoothPrinterPingQueue)) {
        const filteredPing: IStatusCheckBacklog[] = filter(
          this._bluetoothPrinterPingQueue,
          (backlog: IStatusCheckBacklog) => backlog.printerAddress == macAddress_IP,
        );
        each(filteredPing, (backlog: IStatusCheckBacklog) => {
          if (backlog && backlog.resolveFn) {
            backlog.resolveFn({
              success: pingResp.success,
              status: pingResp.message,
            });
          }
        });

        // update ping queue and remove other ping backlog
        this._bluetoothPrinterPingQueue = filter(
          this._bluetoothPrinterPingQueue,
          (backlog: IStatusCheckBacklog) => backlog.printerAddress != macAddress_IP,
        );
      }

      /**
       * Free up the bluetooth module after every ping regardless of result.
       * */
      if (pingResp && printerToUse.Type == PRINTER_TYPE.BT) {
        this._bluetoothConnectionBusy = false;
        this._bluetoothNextJobSubject.next(true);
      }

      pingPrinterResolver({
        success: pingResp.success,
        status: pingResp.message,
      });
    });
  }

  /**
   * Set the printer active state to TRUE
   * @param address The printer address to activate
   * @returns Promise response
   */
  async activatePrinter(address: string): Promise<IPrintResponse> {
    return new Promise(async resolve => {
      if (!address) {
        return;
      }

      // block the screen if necessary
      this._loadingSetNewPrinter(true);

      // grab the printer to activate
      const printerToActivate: Printer = this.getPrinterByAddress(address);

      // check if the printer exists
      if (printerToActivate) {
        // if already active, prevent emitting any events
        if (printerToActivate.Active) {
          // ping the printer
          this.pingPrinterByAddress(address);

          resolve({
            success: true,
            message: 'Printer already active',
          });
        } else {
          printerToActivate.setPrinterActive(true);
          this._countActivePrinters(); // now count
          this.savePrinters(); // update the storage
          this.activatedPrintersChanged(); // fire the event

          // ping the printer
          this.pingPrinterByAddress(address);

          resolve({
            success: true,
            message: printerToActivate.Type == PRINTER_TYPE.BT ? 'BT device connected' : 'IP device connected',
          });
        }

        // THEN - remove the loading
        this._loadingSetNewPrinter(false); // remove the loading
      } else {
        resolve({
          success: false,
          message: PRINTER_STATUS.NOT_FOUND,
        });
      }
      this._loadingSetNewPrinter(false); // remove the loading
    });
  }

  /**************************************************************
   * @desc Disconnect connected printer
   **************************************************************/
  public deactivePrinter(printer: Printer | string): void {
    if (typeof printer == 'string') {
      printer = this.getPrinterByAddress(printer as string);
    }

    if (printer) {
      // exclude from all searches
      printer.setPrinterActive(false);
      this._countActivePrinters(); // now count
      this.savePrinters(); // update the storage
      this.activatedPrintersChanged(); // fire the event
    } else {
      // just ignore for now
    }
  }

  /**
   * Mark the service to loading state while adding a printer
   * @param enabling Flagger to show or hide the loader
   */
  private _loadingSetNewPrinter(enabling: boolean): void {
    this.settingNewPrinter = enabling;
    this.watcher.broadcast(SETTING_ACTIVE_PRINTER, this.settingNewPrinter);
  }

  /**
   * Load saved printer from storage
   */
  async loadSavedPrinters() {
    return new Promise(async resolve => {
      const printers = await this.storage.load(PrinterService.PAIRED_PRINTERS_STORAGE_KEY_2); // paired_printers2
      if (printers) {
        each(printers, device => {
          this.addPrinter(device, device.type);
        });

        this._countActivePrinters(); // now count
        this.activatedPrintersChanged(); // fire the event
      } else {
        // it means it is using an old storage
        const oldPrinters = await this.storage.load(PrinterService.PAIRED_PRINTERS_STORAGE_KEY); // paired_printers
        each(oldPrinters, device => {
          device.pass = true;
          this.addPrinter(device, device.type);
        });

        this._countActivePrinters(); // now count
        this.activatedPrintersChanged(); // fire the event

        // remove the old printers
        await this.storage.remove(PrinterService.PAIRED_PRINTERS_STORAGE_KEY);
      }

      resolve(null);
    });
  }

  /**
   * Counts all active printers
   */
  private _countActivePrinters(): void {
    this.activePrintersCount = size(this.getActivePrinters());
    this._hasActivePrinters = this.activePrintersCount > 0;
  }

  /**
   * Retrieve all pass printers
   * @returns Pass printers
   */
  public getPassPrinters(): Printer[] {
    const method = (printer: Printer) => {
      return printer.Active && printer.Pass;
    };

    // return all pass printers
    return filter(this._availablePrinters, method);
  }

  /**
   * Retrieve all active printers
   * @returns Active printers
   */
  public getActivePrinters(): Printer[] {
    const method = (printer: Printer) => {
      return printer.Active;
    };

    // return all active printers
    return filter(this._availablePrinters, method);
  }

  /**
   * Fires event to indicate printer list have changed
   */
  public activatedPrintersChanged(): void {
    this.watcher.broadcast(ACTIVE_PRINTERS_CHANGED);
  }

  /*** STORING PRINTER ***/
  public addPrinter(printer: any, type: PRINTER_TYPE): Printer | null {
    try {
      let retVal: Printer;
      const selectedPrinter: Printer = this.getPrinterByAddress(printer.address);
      if (!selectedPrinter) {
        printer.type = type;
        retVal = new Printer(printer);
        this._availablePrinters.push(retVal);

        // fail safe to count printers
        this._countActivePrinters();

        retVal.setWatcher(this.watcher);
        this.savePrinters();
      } else {
        retVal = selectedPrinter;
      }

      return retVal;
    } catch (e) {
      console.log('ERROR adding new printer', e);
    }

    return null;
  }

  /**
   * Disconnect and removes printer from the list
   * @param address The bluetooth device MAC Address
   * @returns IPrintReponse result
   */
  public forgetPrinter(address: string): IPrintResponse {
    try {
      // disconnect if it's the connected BT printer
      if (this._bluetoothConnectedAddress && this._bluetoothConnectedAddress == address) {
        this._disconnectBluetoothPrinter();
      }

      const index: number = findIndex(this._availablePrinters, (device: Printer) => device.Address == address);
      if (index > -1) {
        if (this._availablePrinters[index].Type == PRINTER_TYPE.IP) {
          this._availablePrinters[index].dispose();
        }

        this._availablePrinters.splice(index, 1);

        // remove it from all printer groups
        each(this._printerGroups, (group: PrinterGroup) => {
          group.unlinkPrintersByAddress([address]);
          // no need to fire the printer groups change - we are removing the printer anyway
        });

        this.savePrinters();
        this.savePrinterGroups();

        this._countActivePrinters();
        this.activatedPrintersChanged();
        return { success: true, message: '' };
      }

      return { success: false, message: PRINTER_STATUS.NOT_FOUND };
    } catch (e) {
      console.log('ERROR removing printer', e);
      return { success: false, message: PRINTER_STATUS.UNKNOWN };
    }
  }

  /****** SECTION - BLUETOOTH PRINTER ******/
  async connectBluetoothPrinter(address: string, sniffDevicesOnFail: boolean): Promise<IPrintResponse> {
    return new Promise(connectResolve => {
      // check if cordova is enabled
      if (this.platform.is('cordova')) {
        // ANDROID SETUP
        if (this.platform.is('android')) {
          // check bluetooth is enabled
          this.bluetooth
            .isEnabled()
            .then(
              enabled => {
                // ENABLED

                // check if device is paired with a device
                this.bluetooth.isConnected().then(
                  connected => {
                    // PAIRED

                    // no need to disconnect
                    if (address == this._bluetoothConnectedAddress) {
                      connectResolve({
                        success: true,
                        message: PRINTER_MESSAGE.CONNECTED,
                        data: {
                          address: address,
                        },
                      });
                    } else {
                      // if the address doesn't match, then disconnect the active one and connect the new
                      this.sniffDevicesAndConnectAndroid(address, sniffDevicesOnFail).then(resp => {
                        connectResolve(resp);
                      });
                    }
                  },
                  err => {
                    // NOT PAIRED
                    if (this._bluetoothConnectSubscription) this._bluetoothConnectSubscription.unsubscribe();

                    this._bluetoothConnectedAddress = null;
                    this.clearBluetoothBuffer();

                    this.sniffDevicesAndConnectAndroid(address, sniffDevicesOnFail).then(resp => {
                      connectResolve(resp);
                    });
                  },
                );
              },
              failed => {
                // BLUETOOTH IS DISABLED
                this.analytics.trackEvent(
                  'Bluetooth',
                  `Connect failed: ${PRINTER_MESSAGE.BLUETOOTH_DISABLED}`,
                  this.auth.Tracking_key,
                );
                connectResolve({
                  success: false,
                  message: PRINTER_MESSAGE.BLUETOOTH_DISABLED,
                });
              },
            )
            .catch(failed => {
              // BLUETOOTH IS DISABLED
              this.analytics.trackEvent(
                'Bluetooth',
                `Connect failed: ${PRINTER_MESSAGE.BLUETOOTH_DISABLED} (catch)`,
                this.auth.Tracking_key,
              );
              connectResolve({
                success: false,
                message: PRINTER_MESSAGE.BLUETOOTH_DISABLED,
              });
            });
        }
      } else {
        this.analytics.trackEvent(
          'Bluetooth',
          `Connect failed: ${PRINTER_MESSAGE.CORDOVA_NOT_AVAILABLE}`,
          this.auth.Tracking_key,
        );
        connectResolve({
          success: false,
          message: PRINTER_MESSAGE.CORDOVA_NOT_AVAILABLE,
        });
      }
    });
  }

  private _disconnectBluetoothPrinter(): void {
    if (this.platform.is('android')) {
      // check if you are connected first
      this.bluetooth.isConnected().then(
        connected => {
          // if connected, then disconnect
          this.bluetooth.disconnect().then(data => {
            if (this._bluetoothConnectSubscription) this._bluetoothConnectSubscription.unsubscribe();

            this._bluetoothConnectedAddress = null;
          });
        },
        err => {},
      );
    }
  }

  /**************************************************************
   * @desc Get all the devices connected to the device
   **************************************************************/
  public sniffDevicesAndConnectAndroid(address: string, sniffDevicesOnFail: boolean): Promise<IPrintResponse> {
    return new Promise(sniffResolve => {
      // GET LIST OF BLUETOOTH
      this.bluetooth.list().then(
        devices => {
          if (size(devices)) {
            const connectPrinterHandler = (deviceAddress: string): Promise<IPrintResponse> => {
              return new Promise(connectResolver => {
                // CONNECT TO PRINTER
                this._bluetoothConnectSubscription = this.bluetooth.connect(deviceAddress).subscribe(
                  () => {
                    this._bluetoothConnectedAddress = deviceAddress;
                    this.clearBluetoothBuffer();
                    connectResolver({
                      success: true,
                      message: PRINTER_MESSAGE.CONNECTED,
                      data: {
                        address: deviceAddress,
                      },
                    });
                  },
                  fail => {
                    if (this._bluetoothConnectSubscription) this._bluetoothConnectSubscription.unsubscribe();

                    this._bluetoothConnectedAddress = null;
                    this.clearBluetoothBuffer();
                    this._disconnectBluetoothPrinter();
                    this.analytics.trackEvent(
                      'Bluetooth',
                      `Connect failed: ${PRINTER_MESSAGE.CONNECTION_FAILED} | address: ${deviceAddress}`,
                      this.auth.Tracking_key,
                    );
                    connectResolver({
                      success: false,
                      message: PRINTER_MESSAGE.CONNECTION_FAILED,
                    });
                  },
                );
              });
            };

            // IF THERE'S A PRINTER PROVIDED
            if (address) {
              // filter bluetooth printers only
              const method = d => {
                return [1664, 1536, 0].includes(Number(d.class));
              };

              const printerDevices: any[] = filter(devices, method);
              if (size(printerDevices)) {
                each(printerDevices, device => {
                  if (device.address == address) {
                    // disconnect first
                    this.bluetooth.disconnect().then(
                      disconnected => {
                        // IF PRINTER SUCCESSFULLY DISCONNECTED
                        if (this._bluetoothConnectSubscription) this._bluetoothConnectSubscription.unsubscribe();

                        this._bluetoothConnectedAddress = null;

                        // CONNECT TO PRINTER
                        connectPrinterHandler(address).then(resp => {
                          if (resp.success) this.addPrinter(device, PRINTER_TYPE.BT);
                          sniffResolve(resp);
                        });
                      },
                      fail => {
                        // OR IF NOT CONNECTED AT ALL
                        // CONNECT TO PRINTER
                        connectPrinterHandler(address).then(resp => {
                          if (resp.success) this.addPrinter(device, PRINTER_TYPE.BT);
                          sniffResolve(resp);
                        });
                      },
                    );
                  }
                });
              } else {
                this.analytics.trackEvent(
                  'Bluetooth',
                  `Sniff failed: ${PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES} (no printer devices)`,
                  this.auth.Tracking_key,
                );
                sniffResolve({
                  success: false,
                  message: PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES,
                });
              }
            } else {
              if (!sniffDevicesOnFail) {
                this.analytics.trackEvent(
                  'Bluetooth',
                  `Sniff failed: ${PRINTER_MESSAGE.DEVICE_SNIFF_FAILED} (no address)`,
                  this.auth.Tracking_key,
                );
                sniffResolve({
                  success: false,
                  message: PRINTER_MESSAGE.DEVICE_SNIFF_FAILED,
                });
                return;
              }

              // IF THERE'S NO PRINTER ADDRESS PROVIDED
              this.listBluetoothDevices(devices, (printerResp: { printer: any; status: string }) => {
                if (printerResp && printerResp.status == 'success' && printerResp.printer) {
                  this.addPrinter(printerResp.printer, PRINTER_TYPE.BT);
                  sniffResolve({
                    success: true,
                    message: '',
                    data: {
                      address: printerResp.printer.address,
                    },
                  });
                } else {
                  if (printerResp.status == 'empty') {
                    this.analytics.trackEvent(
                      'Bluetooth',
                      `Sniff failed: ${PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES} (user list empty)`,
                      this.auth.Tracking_key,
                    );
                    sniffResolve({
                      success: false,
                      message: PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES,
                    });
                  } else {
                    this.analytics.trackEvent(
                      'Bluetooth',
                      `Sniff failed: ${PRINTER_MESSAGE.NO_PRINTER_SELECTED}`,
                      this.auth.Tracking_key,
                    );
                    sniffResolve({
                      success: false,
                      message: PRINTER_MESSAGE.NO_PRINTER_SELECTED,
                    });
                  }
                }
              });
            }
          } else {
            this.analytics.trackEvent(
              'Bluetooth',
              `Sniff failed: ${PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES} (no devices from list)`,
              this.auth.Tracking_key,
            );
            sniffResolve({
              success: false,
              message: PRINTER_MESSAGE.NO_BLUETOOTH_DEVICES,
            });
          }
        },
        err => {
          this.analytics.trackEvent(
            'Bluetooth',
            `Sniff failed: ${PRINTER_MESSAGE.DEVICE_SNIFF_FAILED} | ${err}`,
            this.auth.Tracking_key,
          );
          sniffResolve({
            success: false,
            message: PRINTER_MESSAGE.DEVICE_SNIFF_FAILED + ': ' + err,
          });
        },
      );
    });
  }

  /**
   * Displays found bluetooth devices
   * @param devices The list of BT devices found
   * @param callback Function to call when a printer is selected
   */
  listBluetoothDevices = async (devices: any[], callback: (params: { printer: any; status: string }) => void) => {
    if (devices && devices.length > 0) {
      // filter bluetooth printers only
      let method = (d: any) => {
        return this.platform.is('ios') || (this.platform.is('android') && [1664, 1536, 0].includes(Number(d.class)));
      };

      let printerDevices: any[] = filter(devices, method);
      if (printerDevices && size(printerDevices) > 0) {
        let alertOptions: any = {
          backdropDismiss: false,
          header: 'Choose a Printer',
          inputs: [],
          buttons: [
            {
              text: 'Cancel',
              cssClass: 'secondary',
              handler: data => {
                // do nothing
                callback({
                  printer: PRINTER_MESSAGE.NO_PRINTER_SELECTED,
                  status: 'empty',
                });
              },
            },
            {
              text: 'Ok',
              handler: data => {
                if (data) {
                  callback({ printer: data, status: 'success' });
                } else {
                  return false;
                }
              },
            },
          ],
        };

        each(printerDevices, (device: any) => {
          alertOptions.inputs.push({
            name: device.name,
            type: 'radio',
            label: device.name,
            value: device,
          });
        });

        let alert = await this.alertCtrl.create(alertOptions);
        this.alertQueue.queueAlert(alert, HubAlertModule.LIST_PRINTER);
      } else {
        callback({ printer: null, status: 'empty' });
      }
    } else {
      callback({ printer: null, status: 'empty' });
    }
  };

  /**
   * Clears bluetooth buffer
   */
  private clearBluetoothBuffer(): Promise<boolean> {
    return new Promise(resolve => {
      if (this.platform.is('android')) {
        // clear the data in the buffer
        this.bluetooth.clear().then(resp => {
          resolve(true);
        });
      }
    });
  }

  /****** PRINTER GROUPS SECTION *******/

  /**
   * Creates all the printer groups of the account
   * @param rawPrinterGroups Printer group obj
   */
  async initialisePrinterGroups(rawPrinterGroups: any[]) {
    return new Promise(async resolve => {
      // clear the array
      this._printerGroups.splice(0);

      // create all the printer groups from the bump screen
      each(rawPrinterGroups, (printerGroup: any) => {
        this._printerGroups.push(new PrinterGroup(printerGroup));
      });

      // load the stored printer group and make sure to attach the child printers
      const storedPrinterGroups: any[] = await this.storage.load(PrinterService.PRINTER_GROUPS_STORAGE_KEY, false);
      each(storedPrinterGroups, storedPrinterGroup => {
        // link the stored printer addresses
        if (size(this._availablePrinters) > 0) {
          const foundGroup: PrinterGroup = this.getPrinterGroupById(storedPrinterGroup.id);
          if (foundGroup) {
            const connectedPrinterAddresses: string[] = this._availablePrinters.map(printer => printer.Address);

            // stored printer cleanup
            if (storedPrinterGroup.printers && storedPrinterGroup.printers.length > 0) {
              const filterPrinterAddress: string[] = filter(
                storedPrinterGroup.printers,
                (printerAddress: string) => connectedPrinterAddresses.indexOf(printerAddress) > -1,
              );
              foundGroup.linkPrintersByAddress(filterPrinterAddress);
            }
          }

          // fire an update on printer level
          this._printersPrinterGroupUpdated(storedPrinterGroup.printers);
        }
      });

      // make sure to update the storage
      this.savePrinterGroups();
      resolve(null);
    });
  }

  /**
   * Save all printer groups to storage
   */
  savePrinterGroups(): void {
    let exportedPrinterGroups: any[] = [];
    each(this._printerGroups, (group: PrinterGroup) => {
      exportedPrinterGroups.push(group.export());
    });

    if (size(exportedPrinterGroups) > 0) {
      this.storage.save(PrinterService.PRINTER_GROUPS_STORAGE_KEY, exportedPrinterGroups, 99, false);
    } else {
      this.storage.remove(PrinterService.PRINTER_GROUPS_STORAGE_KEY, false);
    }
  }

  /**
   * Link Printers to a Printer group. This will create a child-parent relationship.
   * @param printerAddress The collection of printer addresses
   * @param printerGroupID The target Printer Group
   */
  linkPrintersToPrinterGroupByAddress(printerAddress: string[], printerGroupID: string) {
    const foundGroup: PrinterGroup = this.getPrinterGroupById(printerGroupID);
    if (foundGroup) {
      foundGroup.linkPrintersByAddress(printerAddress);
      this.savePrinterGroups();

      // fire an update on printer level
      this._printersPrinterGroupUpdated(printerAddress);
    }
  }

  /**
   * Unlink Printers from a Printer group
   * @param printerAddress The collection of printer addresses
   * @param printerGroupID The target Printer Group
   */
  unlinkPrintersToPrinterGroupByID(printerAddress: string[], printerGroupID: string) {
    const foundGroup: PrinterGroup = this.getPrinterGroupById(printerGroupID);
    if (foundGroup) {
      foundGroup.unlinkPrintersByAddress(printerAddress);
      this.savePrinterGroups();

      // fire an update on printer level
      this._printersPrinterGroupUpdated(printerAddress);
    }
  }

  /**
   * Removes the relationship of Printer device from all parent Printer Groups
   * @param printerAddress The printer address
   */
  unlinkPrintersToAllPrinterGoups(printerAddress: string[]): void {
    each(this._printerGroups, (group: PrinterGroup) => {
      group.unlinkPrintersByAddress(printerAddress);
    });
  }

  /**
   * Event dispatcher to indicate a printer's Printer Groups have changed
   * @param printerAddresses The collection of printer addresses
   */
  private _printersPrinterGroupUpdated(printerAddresses: string[]) {
    each(printerAddresses, (address: string) => {
      const printer: Printer = this.getPrinterByAddress(address);
      if (printer) {
        printer.printerGroupsChanged.next(printer.Address);
      }
    });
  }

  /**
   * Retrieve Printer group by their ID
   * @param id The printer group ID
   * @returns PrinterGroup
   */
  getPrinterGroupById(id: string): PrinterGroup {
    const printerGroupIndex: number = findIndex(
      this._printerGroups,
      (printerGroup: PrinterGroup) => printerGroup.ID == id,
    );
    if (printerGroupIndex > -1) {
      return this._printerGroups[printerGroupIndex];
    }

    return null;
  }

  /**
   * Retrieve Printer groups by their child printers
   * @param printerAddress The list of printer addresses
   * @returns List of PrinterGroup
   */
  getPrinterGroupsByLinkedPrinter(printerAddress: string): PrinterGroup[] {
    let retVal: PrinterGroup[] = [];
    each(this._printerGroups, (printerGroup: PrinterGroup) => {
      if (size(printerGroup.Linked_printers) && printerGroup.Linked_printers.indexOf(printerAddress) > -1) {
        retVal.push(printerGroup);
      }
    });

    return retVal;
  }

  /**
   * Returns the BT connected printer, if available.
   * @returns The printer address
   */
  getConnectedBluetoothDeviceAddress(): string {
    return this._bluetoothConnectedAddress;
  }

  /**
   * Removes all printer groups and clears the job queues, Print and Ping.
   */
  reset(): void {
    // make sure to dispose printers
    each(this._availablePrinters, (printer: Printer) => {
      printer.dispose();
    });

    // clear printing groups
    this._printerGroups.splice(0);

    // stop all queue
    this._bluetoothPrintJobQueue.splice(0);
    this._bluetoothPrinterPingQueue.splice(0);
  }

  /****************************************/
  /*            ERROR CHECKING            */
  /****************************************/
  /**
   * Retrieve printer status by sending a DLE EOT command
   * @param address The address of the target printer
   * @param statusContinueOnSameAddress Ignore the printer busy status if target and connected BT printer is the same
   * @param statusVerifyLastChecked Reverify the printer status
   * @returns Promise response of IPrinterStatus
   */
  private _checkPrinterStatusByAddress(
    printerAddress: string,
    continueOnSameAddress: boolean,
    verifyLastChecked: boolean = false,
  ): Promise<IPrinterStatus> {
    return new Promise(async statusCheckResolver => {
      let printerStatus: IPrinterStatus;
      const printerToCheck: Printer = this.getPrinterByAddress(printerAddress);

      if (size(printerToCheck)) {
        if (verifyLastChecked) {
          const lastChecked = printerToCheck.getLastChecked();
          const now = new Date().valueOf();
          const statusExpired: boolean = !lastChecked || (now - lastChecked) / 1000 >= 30;
          if (!printerToCheck.Active || !statusExpired) {
            console.log('PRINTER TO CHECK - TO VERIFY - SKIPPING:', printerToCheck.Active, statusExpired);
            statusCheckResolver(printerToCheck.Status);
            return;
          }
        }

        if (printerToCheck.Type == PRINTER_TYPE.BT) {
          /****************/
          /*   BT CHECK   */
          /****************/

          // check if bluetooth stream is busy
          if (this._bluetoothConnectionBusy) {
            const sameAddress: boolean = printerAddress == this._bluetoothConnectedAddress;
            if (!sameAddress || !continueOnSameAddress) {
              statusCheckResolver({
                success: false,
                status: PRINTER_STATUS.BUSY,
              });

              return;
            }
          } else {
            // block the bluetooth
            this._bluetoothConnectionBusy = true;
          }

          printerStatus = await this.bluetoothPrinterStatus.checkPrinterStatus(printerToCheck);
        } else if (printerToCheck.Type == PRINTER_TYPE.IP) {
          /****************/
          /*   IP CHECK   */
          /****************/
          printerStatus = await printerToCheck.checkPrinterStatus();
        }
      } else {
        printerStatus = {
          success: false,
          status: PRINTER_STATUS.NOT_FOUND,
        };
      }

      // bubble it up
      // should we clear Queue jobs for same Address?
      statusCheckResolver(printerStatus);
    });
  }

  // mainly used for UNIT TESTING
  // Should be never called outside unit testing
  clearDefaultSubscriptions() {
    if (!environment.unit_test) {
      return;
    }

    this.watcher.suppressAll([this._BTPrinterNextJobSub, this._BTPrinterPingSub, this._BTStatusCheckCompletedSub]);
  }

  /**** GETTER FUNCTIONS */
  public get Has_active_printers(): boolean {
    return this._hasActivePrinters;
  }

  public get Available_printers(): Printer[] {
    return this._availablePrinters;
  }

  public get Available_printers_length(): number {
    return size(this._availablePrinters);
  }

  public get Available_printer_groups(): PrinterGroup[] {
    return this._printerGroups;
  }

  public get Backlog_bluetooth_ping(): IStatusCheckBacklog[] {
    return this._bluetoothPrinterPingQueue;
  }

  public get Backlog_bluetooth_print(): IPrintJobBacklog[] {
    return this._bluetoothPrintJobQueue;
  }

  /** UI PRINTER */

  /**
   * Invokes a connect to network printer process
   */
  async editPrinter(printer: Printer, callback?: () => void) {
    const editNetworkPrinter = await this.modalCtrl.create({
      component: ModalNetworkPrinterComponent,
      backdropDismiss: false,
      animated: true,
      cssClass: 'add-network-printer',
      componentProps: {
        printer: printer,
        edit: true,
        printerGroups: this.Available_printer_groups,
      },
      showBackdrop: true,
    });

    editNetworkPrinter.onDidDismiss().then(value => {
      const { data, role } = value;
      if (data && role == 'save') {
        if (data.address != printer.Address) {
          const targetPrinter: Printer = this.getPrinterByAddress(data.address);
          if (targetPrinter) {
            this.alertCtrl
              .create({
                header: 'Oops!',
                message: `Sorry, the printer address ${data.address} is already in use.`,
                backdropDismiss: false,
                buttons: ['Dismiss'],
              })
              .then(alert => {
                this.alertQueue.queueAlert(alert, HubAlertModule.EDIT_PRINTER);
              });

            // prevent saving duplicate addresses
            return;
          } else {
            // make sure to remove the old address from associated printer groups
            this.unlinkPrintersToAllPrinterGoups([printer.Address]);

            printer.Address = data.address;
            if (printer.Type == PRINTER_TYPE.IP && printer.Active) {
              printer.restartSocket();
            }
          }
        }

        if (data.name) printer.Name = data.name;
        if (data.repeat) printer.Print_repeat = +data.repeat;
        if (printer.Type == PRINTER_TYPE.IP) printer.Port = +data.port;
        printer.Pass = !!data.pass;

        if (data.active) {
          // redundant but this time it will run the ping printer
          this.activatePrinter(printer.Address);
        } else {
          this.deactivePrinter(printer.Address);
        }

        each(data.printerGroupForm, (groupForm: { group: PrinterGroup; isChecked: boolean }) => {
          // call the printer service function here instead of the group
          // to fire the printer groups changed
          if (groupForm.isChecked) {
            this.linkPrintersToPrinterGroupByAddress([printer.Address], groupForm.group.ID);
          } else {
            this.unlinkPrintersToPrinterGroupByID([printer.Address], groupForm.group.ID);
          }
        });

        this.savePrinters();
        this.savePrinterGroups();

        if (callback) {
          callback();
        }
      }
    });

    await editNetworkPrinter.present();
  }
}
